import React, { useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../contexts/SessionContext';
import { Tool } from '../../interfaces';
import useToolUpdate from 'hooks/useToolUpdate';
import useToolDeleteById from 'hooks/useToolDeleteById';
import './Tools.css';

interface ToolsEditControlProps {
    show: boolean;
    handleClose: () => void;
    tool: Tool;
}

const ToolsEditModal: React.FC<ToolsEditControlProps> = ({ show, handleClose, tool }) => {
    const { dispatch } = useSession();
    const { t } = useTranslation();
    const { toolUpdate, error: updateError } = useToolUpdate();
    const { toolDeleteById, error: deleteError } = useToolDeleteById();
    const [urlError, setUrlError] = useState("");
    const [toolData, setToolData] = useState<Tool>(tool);

    const handleChange = (e: React.ChangeEvent<{ name?: string; value: string }>) => {
        const { name, value } = e.target;

        setToolData(prevState => ({
            ...prevState,
            [name as string]: value,
        }));
        if (name === "url" && !/^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i.test(value)) {
            setUrlError(t("tools.invalidUrl"));
        } else {
            setUrlError("");
        }
    };

    const handleUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        const data = await toolUpdate(toolData);
        if (urlError) {
            return;
        }
        if (data) {
            dispatch({ type: 'TOOL_UPDATE', payload: { tool: data } });
            handleClose();
        }
    };

    const handleDelete = async (e: React.FormEvent) => {
        e.preventDefault();
        const isConfirmed = window.confirm(t("tools.deleteConfirmation"));
        if (!isConfirmed) {
            return;
        }

        const data = await toolDeleteById(tool.id);
        if (data) {
            dispatch({ type: 'TOOL_DELETE', payload: { id: data } });
            handleClose();
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            <Modal.Header>
                <Modal.Title className="text-secondary text-center fs-2 fw-semibold position-relative top-0 start-50 translate-middle-x">{t('tools.title')}</Modal.Title>
            </Modal.Header>
            <Form >
                <Modal.Body>
                    <Row className="g-4">
                        <Col md={6}>
                            <Form.Group controlId="formName" className="mb-0 text-nowrap">
                                <Form.Label className="fs-5 fw-semibold text-secondary">{t("name")}</Form.Label>
                                <Form.Control type="text" name="name" value={toolData.name} onChange={handleChange} placeholder={t("tools.enterName")} className='text-secondary bg-light fw-medium' required />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="formURL" className="mb-0 text-nowrap">
                                <Form.Label className="fs-5 fw-semibold text-secondary">URL</Form.Label>
                                <Form.Control type="text" name="url" value={toolData.url} onChange={handleChange} placeholder="https://..." className='text-secondary bg-light fw-medium' required />
                            </Form.Group>
                        </Col>
                        <Col md={12}>
                            <Form.Group controlId="formDescription" className="mb-0 text-nowrap">
                                <Form.Label className="fs-5 fw-semibold text-secondary">{t("description")}</Form.Label>
                                <br />
                                <textarea name="description" value={toolData.description} onChange={handleChange} placeholder={t("tools.enterDescription")} className='text-secondary bg-light fw-medium border-0 w-100 rounded-2 placeholder-textArea ps-2' minLength={5} maxLength={150} required />
                            </Form.Group>
                        </Col>
                        {updateError && <Alert variant="danger" className="fw-medium fs-6 text-center p-2">{updateError}</Alert>}
                        {deleteError && <Alert variant="danger" className="fw-medium fs-6 text-center p-2">{deleteError}</Alert>}
                        {urlError && <Alert variant='danger' className="fw-medium fs-6 text-center p-2">{urlError}</Alert>}
                    </Row>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between w-100">
                    <Button type="button" variant="danger" className="py-2 px-3 fs-5" onClick={handleDelete}>{t("delete")}</Button>
                    <div className="gap-1">
                        <Button type="submit" variant="secondary" className="py-2 px-3 fs-5 me-3" onClick={handleUpdate}>{t("submit")}</Button>
                        <Button variant="primary" className="py-2 px-3 fs-5" onClick={handleClose}>{t("close")}</Button>
                    </div>
                </Modal.Footer>
            </Form>

        </Modal>
    );

};
export default ToolsEditModal;